/* eslint-disable consistent-return */
import { Meteor } from 'meteor/meteor';
import { check } from 'meteor/check';
import { HTTP } from 'meteor/http';
import { Client } from 'pg';
import { google } from 'googleapis';
import fs from 'fs-extra';
import path from 'path';
import {
  concat,
  filter,
  find,
  forEach,
  get,
  includes,
  isArray,
  isEmpty,
  isObject,
  isString,
  keys,
  map,
  mapValues,
  split
} from 'lodash';
import turf from '@turf/turf';
import btoaMethod from 'btoa';

import * as layerOps from '../layer-operations';
import Layers, { GEOREF_TYPES, FIWARE_VERSIONS } from '../Layers';
import { FeaturePoint } from '../../SchemaUtilities/GeoJSONSchema';

import { isHTTPResultOK } from '../../../modules/fetch-utilites';
import handleMethodException from '../../../modules/handle-method-exception';
import checkAuthentication from '../../../modules/server/check-authentication';
import methodPromise from '../../../modules/method-promise-wrapper';

const {
  config: { externalIP: EXTERNAL_IP },
  inverseGeocodingAPI: INVERSE_GEOCODING_API,
  getCatAPI: CAT_API,
  reroutings
} = Meteor.settings.private;

const FIWARE_ERROR_MESSAGE = 'Expecting to find X-Auth-token/Authorization in requests';

const editLayerSchema = Layers.schema.omit(
  'owner',
  'createdAt',
  'updatedAt',
  'deleted',
  'sharedData',
  'sharedPrivateData',
  'publicDataId'
);
editLayerSchema.extend({ _id: String });

/**
 * @typedef {object} LayerQuery
 * @property {number} totalCount The total number of layers not in the trash.
 * @property {number} deletedCount The total number of layers in the trash.
 * @property {object[]} layers The layers not in the trash in the current page.
 * @property {object[]} deletedLayers The layers in the trash.
 */

/**
 * Fetchs one layer from the database.
 *
 * @param {string} layerId - The layer id.
 * @returns {object} Return the layer from the database.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const fetchOne = layerId => {
  try {
    // FIXME: Seguro que hay alguna forma mejor pero esto es lo que se me ocurre
    // checkAuthentication();
    return Layers.findOne(
      { _id: { $eq: layerId }, deleted: { $eq: 'no' } },
      {
        fields: {
          // TODO: poner los campos que no quiero compartir.
          owner: 0
        }
      }
    );
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Inserts the layer in the database.
 *
 * @async
 * @param {object} layerObject - The layer object.
 * @param {string} userId - The user id.
 * @returns {string} Return the id of the new layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const insert = async (layerObject, userId) => {
  check(userId, String);
  try {
    checkAuthentication();
    Layers.schema.validate(layerObject, {
      check,
      keys: [
        'annotationLayerProperties',
        'attribution',
        'connectionParams',
        'description',
        'excelLimits',
        'excelSheet',
        'fiwareParams',
        'geojsonGeometry',
        'geojsonId',
        'geojsonProps',
        'geojsonPropToJoin',
        'georefType',
        'h3Index',
        'imageFormat',
        'imageUrl',
        'interval',
        'layerNames',
        'layerSubType',
        'layerTitles',
        'layerType',
        'mapboxLayerName',
        'mapboxProps',
        'mapLocation',
        'name',
        'ogrAttributes',
        'ogrLayerName',
        'separator',
        'spatialTableFields',
        'styleName',
        'styleId',
        'subdomains',
        'thematicId',
        'thematicKeyList',
        'thematicKeyToJoin',
        'tileMatrixSet',
        'url',
        'wmsVersion',
        'wmtsLayer',
        'wmtsStyle'
      ]
    });
    return Layers.insert({ owner: userId, ...layerObject });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates the layer in the database.
 *
 * @param {object} layerObject - The layer object.
 * @returns {string} Return the id of the updated layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const update = layerObject => {
  try {
    checkAuthentication();
    editLayerSchema.validate(layerObject);
    const layerId = layerOps.getId(layerObject);
    Layers.update(layerId, { $set: layerObject });
    return layerId;
    // Return _id so we can redirect to layer after update.
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Copies the layer in the database.
 *
 * @param {string} layerId - The layer id.
 * @param {string} userId - The user id that wants the layer.
 * @returns {string} Return the id of the new layer copied.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const copy = (layerId, userId) => {
  check(layerId, String);
  check(userId, String);
  try {
    checkAuthentication();
    const copyLayerData = Layers.findOne({ _id: layerId });
    delete copyLayerData.owner;
    delete copyLayerData._id;
    delete copyLayerData.createdAt;
    delete copyLayerData.updatedAt;
    delete copyLayerData.sharedData;
    delete copyLayerData.sharedPrivateData;
    delete copyLayerData.publicDataId;
    delete copyLayerData.deleted;
    copyLayerData.name += ' - copy';
    const copyLayerId = Layers.insert({ owner: userId, ...copyLayerData });
    return copyLayerId;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Soft deletes the layer in the database.
 *
 * @param {string} layerId - The layer id.
 * @returns {string} Return the id of the layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const softDelete = layerId => {
  check(layerId, String);
  try {
    checkAuthentication();
    return Layers.update(layerId, { $set: { deleted: new Date().toISOString() } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Restores the layer in the database.
 *
 * @param {string} layerId - The layer id.
 * @returns {string} Return the id of the layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const restore = layerId => {
  check(layerId, String);
  try {
    checkAuthentication();
    return Layers.update(layerId, { $set: { deleted: 'no' } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Deletes the layer from database.
 *
 * @param {string} layerId - The layer id.
 * @returns {string} Return the id of the deleted layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const hardDelete = layerId => {
  check(layerId, String);
  try {
    checkAuthentication();
    return Layers.remove(layerId);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the layer data shared or unshared.
 *
 * @param {string} layerId - The layer id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedLayerData = (layerId, isShared) => {
  check(layerId, String);
  check(isShared, Boolean);
  try {
    checkAuthentication();
    Layers.update(layerId, { $set: { sharedData: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Toggles the layer data shared or unshared.
 *
 * @param {string} layerId - The layer id.
 * @param {boolean} isShared - The new value for the shared key.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const toggleSharedPrivateLayerData = (layerId, isShared) => {
  check(layerId, String);
  check(isShared, Boolean);
  try {
    checkAuthentication();
    Layers.update(layerId, { $set: { sharedPrivateData: isShared } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates Layer map location on user request.
 *
 * @param {string} layerId - The layer id.
 * @param {FeaturePoint} newMapLocation - The new map location.
 * @returns {string} Return the id of the updated layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateMapLocation = (layerId, newMapLocation) => {
  check(layerId, String);
  try {
    checkAuthentication();
    FeaturePoint.validate(newMapLocation);
    return Layers.update(layerId, { $set: { mapLocation: newMapLocation } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates Layer map location on user request.
 *
 * @param {string} layerId - The layer id.
 * @param {Array} newAnnotationsId - The new array of the ids.
 * @returns {string} Return the id of the updated layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateAnnotationsRealTime = (layerId, newAnnotationsId) => {
  check(layerId, String);
  try {
    checkAuthentication();
    return Layers.update(layerId, {
      $set: { 'annotationLayerProperties.annotationsId': newAnnotationsId }
    });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Updates Layer style on user request.
 *
 * @param {string} layerId - The layer id.
 * @param {string} newStyleId - The id of the new style.
 * @returns {string} Return the id of the updated layer.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const updateStyle = (layerId, newStyleId) => {
  check(layerId, String);
  try {
    checkAuthentication();
    return Layers.update(layerId, { $set: { styleId: newStyleId } });
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Fetches the version auth token for a Fiware connection.
 *
 * @param {object} connectionParameters - The connection parameters.
 * @returns {string} The Fiware auth token.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getTokenVersion = connectionParameters => {
  const { fiwareTokenURL } = connectionParameters;
  try {
    const response = HTTP.get(fiwareTokenURL, { headers: { 'Content-Type': 'application/json' } });
    const responseBody = isHTTPResultOK(response);
    const versionTotal = responseBody.version.id;
    const versionSplit = versionTotal.split('.');
    return versionSplit[0];
  } catch (exception) {
    if (
      get(exception, 'response.statusCode') === 400 &&
      get(exception, 'response.data.error.message', '') === FIWARE_ERROR_MESSAGE
    )
      return FIWARE_VERSIONS.V1;

    handleMethodException(exception);
  }
};

/**
 * Fetches the auth token for a Fiware connection.
 *
 * @param {object} connectionParameters - The connection parameters.
 * @returns {string} The Fiware auth token.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const getFiwareToken = connectionParameters => {
  const {
    password,
    service,
    subservice,
    user,
    fiwareTokenURL,
    fiwareApplicationID,
    fiwareApplicationSecret,
    fiwareVersion
  } = connectionParameters;
  try {
    let token = '';
    if (fiwareVersion === FIWARE_VERSIONS.V3) {
      const response = HTTP.post(`${fiwareTokenURL}auth/tokens`, {
        headers: { 'Content-Type': 'application/json' },
        data: {
          auth: {
            identity: {
              methods: ['password'],
              password: { user: { domain: { name: service }, name: user, password } }
            },
            scope: { project: { domain: { name: service }, name: subservice } }
          }
        }
      });
      token = response.headers['x-subject-token'];
    } else if (fiwareVersion === FIWARE_VERSIONS.V1) {
      const encodedTokenAuth = btoaMethod(`${fiwareApplicationID}:${fiwareApplicationSecret}`);
      const host = fiwareTokenURL.split('/')[2];
      const tokenHost = fiwareTokenURL.split('/v')[0];
      const tokenURL = `${tokenHost}/oauth2/token`;
      const response = HTTP.post(tokenURL, {
        headers: {
          Host: host,
          Authorization: `Basic ${encodedTokenAuth}`,
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        params: { username: user, password, grant_type: 'password' }
      });
      const responseJSON = JSON.parse(response.content);
      token = responseJSON.access_token;
    } else {
      throw Error('Versión no disponible');
    }
    return token;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Fetches the Fiware layer geojson.
 *
 * @param {object} arg - The object parameter.
 * @param {object} arg.fiwareLayerName - The name of the layer/type to fetch.
 * @param {object} arg.loadParameters - The load parameters to extract the data.
 * @param {object} [arg.georefType] - The geocoding type of the layer.
 * @param {boolean} [arg.isThematic] - The flag to check if the return will be an object or an Array.
 * @param {object} [arg.spatialTableFields] - The attributes to fetch.
 * @returns {object|Array} The geojson or the table.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadFiware = async ({
  fiwareLayerName,
  loadParameters,
  georefType = '',
  isThematic = false,
  spatialTableFields = {},
  locationIndex = '',
  addressIndex = ''
}) => {
  try {
    const { fiwareURL, fiwareVersion, host, port, service, subservice } = loadParameters;
    const token = getFiwareToken(loadParameters);
    const url = `http://${host}:${port}${fiwareURL}${
      fiwareURL.match(/\?/) ? '&' : '?'
    }type=${fiwareLayerName}`;
    const headers = {
      'Fiware-Service': service,
      'Fiware-ServicePath': subservice,
      'X-Auth-Token': token
    };
    if (fiwareVersion === FIWARE_VERSIONS.V3) headers['Content-Type'] = 'application/json';

    const response = HTTP.get(url, { headers });
    const json = isHTTPResultOK(response);
    const entities = isArray(json) ? json : [json];

    if (isThematic) {
      let attributes = [];
      forEach(entities, element => {
        const elementAttributes = keys(element);
        const newKeys = filter(elementAttributes, key => !includes(attributes, key));
        attributes = concat(attributes, newKeys);
      });
      const thematicTable = map(entities, element => {
        const row = [];
        forEach(attributes, attribute => {
          if (isObject(element[attribute])) row.push(JSON.stringify(element[attribute].value));
          else row.push(element[attribute]);
        });
        return row;
      });
      return [attributes, ...thematicTable];
    }

    const features = map(entities, element => {
      const properties = mapValues(element, key => {
        if (isObject(key.value)) return JSON.stringify(key.value);
        if (isObject(key)) return JSON.stringify(key.value);
        return key;
      });
      let address = '';
      let feature = {};
      let geocodingResponse = {};
      let longitude = 0;
      let latitude = 0;
      switch (georefType) {
        case GEOREF_TYPES.LOCATION:
          feature = turf.feature(element[locationIndex].value, properties);
          break;
        case GEOREF_TYPES.LONGLAT:
          longitude = element[spatialTableFields.longitude].value;
          latitude = element[spatialTableFields.latitude].value;
          feature = turf.point([longitude, latitude], properties);
          break;
        case GEOREF_TYPES.ADDRESS:
          address = element[addressIndex].value;
          geocodingResponse = HTTP.post(INVERSE_GEOCODING_API, {
            data: {
              user: '',
              input: isString(address)
                ? address
                : `${address.addressLocality} ${address.streetAddress}`
            }
          });
          [{ lon: longitude, lat: latitude }] = isHTTPResultOK(geocodingResponse);
          feature = turf.point([longitude, latitude], properties);
          break;
        default:
          feature = turf.point([longitude, latitude], properties);
          break;
      }
      return feature;
    });

    let geojson = {};
    if (!isEmpty(features)) geojson = turf.featureCollection(features);
    return geojson;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Fetches the attributes of a layer/type for a Fiware connection.
 *
 * @param {object} arg - The object parameter.
 * @param {object} arg.fiwareLayerName - The name of the layer/type to fetch.
 * @param {object} arg.loadParameters - The load parameters to extract the data.
 * @returns {string[]} The attributes of the layer/type.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadFiwareAttributes = async ({ fiwareLayerName, loadParameters }) => {
  try {
    const { fiwareURL, fiwareVersion, host, port, service, subservice } = loadParameters;
    const token = getFiwareToken(loadParameters);
    const url = `http://${host}:${port}${fiwareURL}${
      fiwareURL.match(/\?/) ? '&' : '?'
    }type=${fiwareLayerName}`;
    const headers = {
      'Fiware-Service': service,
      'Fiware-ServicePath': subservice,
      'X-Auth-Token': token
    };
    if (fiwareVersion === FIWARE_VERSIONS.V3) headers['Content-Type'] = 'application/json';

    const response = HTTP.get(url, { headers });
    const json = isHTTPResultOK(response);
    const entities = isArray(json) ? json : [json];

    let attributes = [];
    forEach(entities, element => {
      const elementAttributes = keys(element);
      const newKeys = filter(elementAttributes, key => !includes(attributes, key));
      attributes = concat(attributes, newKeys);
    });

    return attributes;
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Fetches the layers/types for a Fiware connection.
 *
 * @param {object} loadParameters - The load parameters to extract the data.
 * @returns {string[]} The names of the layers/types.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadFiwareLayers = async loadParameters => {
  try {
    const { fiwareVersion, host, port, service, subservice } = loadParameters;
    const token = getFiwareToken(loadParameters);
    const url = `http://${host}:${port}/v2/types?options=values`;
    const headers = {
      'Fiware-Service': service,
      'Fiware-ServicePath': subservice,
      'X-Auth-Token': token
    };
    if (fiwareVersion === FIWARE_VERSIONS.V3) headers['Content-Type'] = 'application/json';

    const response = HTTP.get(url, { headers });
    return isHTTPResultOK(response);
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Loads the Google Spreadsheet.
 *
 * @param {string} arg - The object parameter.
 * @param {string} arg.spreadsheetId - The Google Spreadsheet id.
 * @param {string} [arg.excelSheet] - The name of the sheet to extract.
 * @returns {string[]|string[][]} An array containing the sheet list or an array containing the rows.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadGoogleSheets = async ({ spreadsheetId, excelSheet = '' }) => {
  const privateKeyFilePath = path.join(
    Meteor.settings.private.config.dataPath,
    'ipsilum-google-api-private-key.json'
  );
  try {
    if (fs.existsSync(privateKeyFilePath)) {
      const auth = new google.auth.GoogleAuth({
        keyFile: privateKeyFilePath,
        scopes: 'https://www.googleapis.com/auth/spreadsheets'
      });
      const authClient = await auth.getClient();
      const googleSheets = await google.sheets({ version: 'v4', auth: authClient });
      const spreadsheets = await googleSheets.spreadsheets.get({ auth, spreadsheetId });

      if (isEmpty(excelSheet))
        return map(spreadsheets.data.sheets, ({ properties: { title } }) => title);

      const sheet = find(spreadsheets.data.sheets, ['properties.title', excelSheet]) || {};
      const { rowCount, columnCount } = get(sheet, 'properties.gridProperties', {});
      const values = await googleSheets.spreadsheets.values.get({
        auth,
        spreadsheetId,
        range: `${excelSheet}!R1C1:R${rowCount}C${columnCount}`
      });
      return get(values, 'data.values', []);
    }
  } catch (exception) {
    handleMethodException(exception);
  }
};

/**
 * Checks an url for the external IP or domain and replaces it with the internal IP.
 *
 * @param {string} url - The url to adapt.
 * @param {string} OGR_API - The url API.
 * @returns {string} The adapted url.
 */
const adaptExternalURL = async (url, OGR_API) => {
  const INTERNAL_IP = split(split(OGR_API, '/')[2], ':')[0];
  let adaptedURL = url.replace(EXTERNAL_IP, INTERNAL_IP);
  forEach(reroutings, (internalURL, domain) => {
    const domainRegEx = new RegExp(domain);
    if (domainRegEx.test(url)) adaptedURL = url.replace(domain, internalURL);
  });
  return adaptedURL;
};

/**
 * Fetches the OGR geojson.
 *
 * @param {object} loadObject - The object parameter.
 * @returns {object} The geojson or the table.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadOGRGeoJSON = async loadObject => {
  let OGR_API = '';

  const { url } = loadObject;
  try {
    const APISettings = await methodPromise('admin.fetchAPISetting');
    if (APISettings.length > 0) {
      OGR_API = APISettings[0].OGRLayerAPI;
    }
    const adaptedURL = await adaptExternalURL(url, OGR_API);
    const response = await HTTP.post(OGR_API, {
      headers: { 'Content-Type': 'application/json' },
      data: { user: Meteor.userId(), ...loadObject, url: adaptedURL }
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};

/**
 * Fetches the PostgreSQL table or geojson.
 *
 * @param {object} arg - The object parameter.
 * @param {object} arg.loadParameters - The load parameters to extract the data.
 * @param {object} arg.attributes - The attributes to fetch.
 * @param {boolean} [arg.isThematic] - The flag to check if the return will be an object or an Array.
 * @returns {object|Array} The geojson or the table.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadPostgreSQL = async ({ loadParameters, attributes, isThematic }) => {
  const { schema, table } = loadParameters;
  const client = new Client(loadParameters);
  try {
    await client.connect();
    const geometryQuery = {
      text: `select column_name from (SELECT a.attname as column_name, pg_catalog.format_type(a.atttypid, a.atttypmod) FROM pg_attribute a JOIN pg_class t on a.attrelid = t.oid JOIN pg_namespace s on t.relnamespace = s.oid WHERE a.attnum > 0 AND NOT a.attisdropped AND t.relname = '${table}' AND s.nspname = '${schema}' ORDER BY a.attnum)a where format_type like '%geometry%';`,
      rowMode: 'array'
    };
    const geometryResponse = await client.query(geometryQuery);
    const geometryName = get(geometryResponse, ['rows', '0', '0'], '');
    if (isEmpty(geometryName) && !isThematic) throw new Meteor.Error('Layer has no geometry!!');
    const attributesString = `"${attributes.join('","')}"`;

    if (isThematic) {
      const thematicQuery = {
        text: `select ${attributesString} from "${schema}"."${table}";`,
        rowMode: 'array'
      };
      const thematicResponse = await client.query(thematicQuery);
      const thematicTable = thematicResponse.rows;
      await client.end();
      return [attributes, ...thematicTable];
    }

    const geojsonQuery = {
      text: `(SELECT 'FeatureCollection' As type, array_to_json(array_agg(f)) As features FROM (SELECT 'Feature' As type, ST_AsGeoJSON(ST_Transform(${geometryName},4326),15,0)::json As geometry, row_to_json( (SELECT r FROM  (select ${attributesString})r) ) As properties FROM  "${schema}"."${table}") As f );`
    };
    const geojsonResponse = await client.query(geojsonQuery);
    const geojson = geojsonResponse.rows[0];
    await client.end();
    return geojson;
  } catch (exception) {
    await client.end();
    handleMethodException(exception);
  }
};

/**
 * Fetches the PostgreSQL attributes.
 *
 * @param {object} arg - The object parameter.
 * @param {object} arg.loadParameters - The load parameters to extract the data.
 * @param {boolean} [arg.isThematic] - The flag to check if the return will be an object or an Array.
 * @returns {Array} The attributes array.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadPostgreSQLAttributes = async ({ loadParameters, isThematic }) => {
  const { schema, table } = loadParameters;
  const client = new Client(loadParameters);
  try {
    checkAuthentication();
    await client.connect();
    const geometryQuery = {
      text: `select column_name from (SELECT a.attname as column_name, pg_catalog.format_type(a.atttypid, a.atttypmod) FROM pg_attribute a JOIN pg_class t on a.attrelid = t.oid JOIN pg_namespace s on t.relnamespace = s.oid WHERE a.attnum > 0 AND NOT a.attisdropped AND t.relname = '${table}' AND s.nspname = '${schema}' ORDER BY a.attnum)a where format_type like '%geometry%';`,
      rowMode: 'array'
    };
    const geometryResponse = await client.query(geometryQuery);
    const geometryName = get(geometryResponse, ['rows', '0', '0'], '');
    if (isEmpty(geometryName) && !isThematic) throw new Meteor.Error('Layer has no geometry!!');

    const attributesQuery = {
      text: `SELECT a.attname as column_name FROM pg_attribute a JOIN pg_class t on a.attrelid = t.oid JOIN pg_namespace s on t.relnamespace = s.oid WHERE a.attnum > 0 AND NOT a.attisdropped AND t.relname = '${table}' AND s.nspname = '${schema}' ORDER BY a.attnum;`,
      rowMode: 'array'
    };
    const attributesResponse = await client.query(attributesQuery);
    const attributes = map(
      filter(attributesResponse.rows, attributeArray => attributeArray[0] !== geometryName),
      attributeArray => attributeArray[0]
    );
    await client.end();
    return attributes;
  } catch (exception) {
    await client.end();
    handleMethodException(exception);
  }
};

/**
 * Fetches the OGR geojson.
 *
 * @param {object} loadObject - The object parameter.
 * @returns {object} The geojson or the table.
 * @throws {Error} Meteor Error if anything goes wrong.
 */
export const loadCatalogo = async loadObject => {
  try {
    const response = HTTP.post(CAT_API, {
      headers: { 'Content-Type': 'application/json' },
      data: {
        user: Meteor.userId(),
        ...loadObject
      }
    });
    return isHTTPResultOK(response);
  } catch (error) {
    handleMethodException(error);
  }
};
